/*
All material copyright ESRI, All Rights Reserved, unless otherwise specified.
See https://js.arcgis.com/4.30/esri/copyright.txt for details.
*/
import{ShaderOutput as e}from"../views/3d/webgl-engine/core/shaderLibrary/ShaderOutput.js";import{SliceDraw as o}from"../views/3d/webgl-engine/core/shaderLibrary/Slice.glsl.js";import{Transform as r}from"../views/3d/webgl-engine/core/shaderLibrary/Transform.glsl.js";import{ObjectAndLayerIdColor as t}from"../views/3d/webgl-engine/core/shaderLibrary/attributes/ObjectAndLayerIdColor.glsl.js";import{VertexColor as a}from"../views/3d/webgl-engine/core/shaderLibrary/attributes/VertexColor.glsl.js";import{OutputHighlight as i}from"../views/3d/webgl-engine/core/shaderLibrary/output/OutputHighlight.glsl.js";import{multipassTerrainTest as l}from"../views/3d/webgl-engine/core/shaderLibrary/shading/MultipassTerrainTest.glsl.js";import{VisualVariables as n}from"../views/3d/webgl-engine/core/shaderLibrary/shading/VisualVariables.glsl.js";import{symbolAlphaCutoff as c}from"../views/3d/webgl-engine/core/shaderLibrary/util/AlphaCutoff.js";import{ColorConversion as d}from"../views/3d/webgl-engine/core/shaderLibrary/util/ColorConversion.glsl.js";import{addProjViewLocalOrigin as s,addCameraPosition as p}from"../views/3d/webgl-engine/core/shaderLibrary/util/View.glsl.js";import{Float4PassUniform as g}from"../views/3d/webgl-engine/core/shaderModules/Float4PassUniform.js";import{FloatPassUniform as v}from"../views/3d/webgl-engine/core/shaderModules/FloatPassUniform.js";import{glsl as u}from"../views/3d/webgl-engine/core/shaderModules/interfaces.js";import{ShaderBuilder as m}from"../views/3d/webgl-engine/core/shaderModules/ShaderBuilder.js";import{TransparencyPassType as f}from"../views/3d/webgl-engine/lib/TransparencyPassType.js";import{VertexAttribute as w}from"../views/3d/webgl-engine/lib/VertexAttribute.js";import{Style as h}from"../views/3d/webgl-engine/materials/PatternStyle.js";const b=.70710678118,y=b,C=.08715574274;function S(S){const T=new m,x=S.multipassEnabled&&S.output===e.Color,{vertex:P,fragment:R,attributes:A,varyings:V}=T;s(P,S),T.include(r,S),T.include(a,S),T.include(n,S),T.include(t,S),S.draped?P.uniforms.add(new v("worldToScreenRatio",((e,o)=>1/o.screenToPCSRatio))):A.add(w.BOUNDINGRECT,"mat3"),A.add(w.POSITION,"vec3"),A.add(w.UVMAPSPACE,"vec4"),S.vvColor&&A.add(w.COLORFEATUREATTRIBUTE,"float"),V.add("vColor","vec4"),V.add("vpos","vec3"),V.add("vuv","vec2"),x&&V.add("depth","float"),P.uniforms.add(new g("uColor",(e=>e.color)));const $=S.style===h.ForwardDiagonal||S.style===h.BackwardDiagonal||S.style===h.DiagonalCross;return $&&P.code.add(u`
      const mat2 rotate45 = mat2(${u.float(b)}, ${u.float(-y)},
                                 ${u.float(y)}, ${u.float(b)});
    `),S.draped||(p(P,S),P.uniforms.add(new v("worldToScreenPerDistanceRatio",((e,o)=>1/o.camera.perScreenPixelRatio))),P.code.add(u`vec3 projectPointToLineSegment(vec3 center, vec3 halfVector, vec3 point) {
float projectedLength = dot(halfVector, point - center) / dot(halfVector, halfVector);
return center + halfVector * clamp(projectedLength, -1.0, 1.0);
}`),P.code.add(u`vec3 intersectRayPlane(vec3 rayDir, vec3 rayOrigin, vec3 planeNormal, vec3 planePoint) {
float d = dot(planeNormal, planePoint);
float t = (d - dot(planeNormal, rayOrigin)) / dot(planeNormal, rayDir);
return rayOrigin + t * rayDir;
}`),P.code.add(u`
      float boundingRectDistanceToCamera() {
        vec3 center = vec3(boundingRect[0][0], boundingRect[0][1], boundingRect[0][2]);
        vec3 halfU = vec3(boundingRect[1][0], boundingRect[1][1], boundingRect[1][2]);
        vec3 halfV = vec3(boundingRect[2][0], boundingRect[2][1], boundingRect[2][2]);
        vec3 n = normalize(cross(halfU, halfV));

        vec3 viewDir = - vec3(view[0][2], view[1][2], view[2][2]);

        float viewAngle = dot(viewDir, n);
        float minViewAngle = ${u.float(C)};

        if (abs(viewAngle) < minViewAngle) {
          // view direction is (almost) parallel to plane -> clamp it to min angle
          float normalComponent = sign(viewAngle) * minViewAngle - viewAngle;
          viewDir = normalize(viewDir + normalComponent * n);
        }

        // intersect view direction with infinite plane that contains bounding rect
        vec3 planeProjected = intersectRayPlane(viewDir, cameraPosition, n, center);

        // clip to bounds by projecting to u and v line segments individually
        vec3 uProjected = projectPointToLineSegment(center, halfU, planeProjected);
        vec3 vProjected = projectPointToLineSegment(center, halfV, planeProjected);

        // use to calculate the closest point to camera on bounding rect
        vec3 closestPoint = uProjected + vProjected - center;

        return length(closestPoint - cameraPosition);
      }
    `)),P.code.add(u`
    vec2 scaledUV() {
      vec2 uv = uvMapSpace.xy ${$?" * rotate45":""};
      vec2 uvCellOrigin = uvMapSpace.zw ${$?" * rotate45":""};

      ${S.draped?"":u`
            float distanceToCamera = boundingRectDistanceToCamera();
            float worldToScreenRatio = worldToScreenPerDistanceRatio / distanceToCamera;
          `}

      // Logarithmically discretize ratio to avoid jittering
      float step = 0.1;
      float discreteWorldToScreenRatio = log(worldToScreenRatio);
      discreteWorldToScreenRatio = ceil(discreteWorldToScreenRatio / step) * step;
      discreteWorldToScreenRatio = exp(discreteWorldToScreenRatio);

      vec2 uvOffset = mod(uvCellOrigin * discreteWorldToScreenRatio, ${u.float(S.patternSpacing)});
      return uvOffset + (uv * discreteWorldToScreenRatio);
    }
  `),P.code.add(u`
    void main(void) {
      vuv = scaledUV();
      vpos = position;
      ${x?"depth = (view * vec4(vpos, 1.0)).z;":""}
      forwardNormalizedVertexColor();
      forwardObjectAndLayerIdColor();
      ${S.hasVertexColors?"vColor *= uColor;":S.vvColor?"vColor = uColor * interpolateVVColor(colorFeatureAttribute);":"vColor = uColor;"}
      gl_Position = transformPosition(proj, view, vpos);
    }
  `),T.include(o,S),R.include(d),S.draped&&R.uniforms.add(new v("texelSize",((e,o)=>1/o.camera.pixelRatio))),S.output===e.Highlight&&T.include(i,S),x&&T.include(l,S),S.output!==e.Highlight&&(R.code.add(u`
      const float lineWidth = ${u.float(S.lineWidth)};
      const float spacing = ${u.float(S.patternSpacing)};
      const float spacingINV = ${u.float(1/S.patternSpacing)};

      float coverage(float p, float txlSize) {
        p = mod(p, spacing);

        float halfTxlSize = txlSize / 2.0;

        float start = p - halfTxlSize;
        float end = p + halfTxlSize;

        float coverage = (ceil(end * spacingINV) - floor(start * spacingINV)) * lineWidth;
        coverage -= min(lineWidth, mod(start, spacing));
        coverage -= max(lineWidth - mod(end, spacing), 0.0);

        return coverage / txlSize;
      }
    `),S.draped||R.code.add(u`const int maxSamples = 5;
float sampleAA(float p) {
vec2 dxdy = abs(vec2(dFdx(p), dFdy(p)));
float fwidth = dxdy.x + dxdy.y;
ivec2 samples = 1 + ivec2(clamp(dxdy, 0.0, float(maxSamples - 1)));
vec2 invSamples = 1.0 / vec2(samples);
float accumulator = 0.0;
for (int j = 0; j < maxSamples; j++) {
if(j >= samples.y) {
break;
}
for (int i = 0; i < maxSamples; i++) {
if(i >= samples.x) {
break;
}
vec2 step = vec2(i,j) * invSamples - 0.5;
accumulator += coverage(p + step.x * dxdy.x + step.y * dxdy.y, fwidth);
}
}
accumulator /= float(samples.x * samples.y);
return accumulator;
}`)),S.transparencyPassType===f.ColorAlpha&&(T.outputs.add("fragColor","vec4",0),T.outputs.add("fragAlpha","float",1)),R.code.add(u`
    void main() {
      discardBySlice(vpos);
      ${x?"terrainDepthTest(depth);":""}
      vec4 color = vColor;
      color = highlightSlice(color, vpos);

      ${S.output!==e.Highlight?u`color.a *= ${j(S)};`:""}

      ${S.output===e.ObjectAndLayerIdColor?u`color.a = 1.0;`:""}

      if (color.a < ${u.float(c)}) {
        discard;
      }

      ${S.output===e.Color?u`fragColor = color; ${S.transparencyPassType===f.ColorAlpha?u`
                    fragColor = premultiplyAlpha(fragColor);
                    fragAlpha = fragColor.a;`:""}`:""}
      ${S.output===e.Highlight?u`outputHighlight();`:""}
      ${S.output===e.ObjectAndLayerIdColor?u`outputObjectAndLayerIdColor();`:""}
    }
  `),T}function j(e){function o(o){return e.draped?u`coverage(vuv.${o}, texelSize)`:u`sampleAA(vuv.${o})`}switch(e.style){case h.ForwardDiagonal:case h.Horizontal:return o("y");case h.BackwardDiagonal:case h.Vertical:return o("x");case h.DiagonalCross:case h.Cross:return u`
        1.0 - (1.0 - ${o("x")}) * (1.0 - ${o("y")})
      `;default:return"0.0"}}const T=Object.freeze(Object.defineProperty({__proto__:null,build:S},Symbol.toStringTag,{value:"Module"}));export{T as P,S as b};
